<template>
  <div>
    <v-card flat>
      <v-card-text>
        <v-row dense>
          <v-col
            v-for="cert in items"
            :key="cert.id"
            cols="12"
            sm="6"
            md="4"
            class="d-flex align-items-stretch"
          >
            <v-card variant="flat" class="my-4 flex-grow-1 app-content-card">
              <v-card-text class="text-left">
                <h3>{{ $t("candidateTabs.certifications.field") }}: {{ cert.Certificate }}</h3>
                <v-divider></v-divider>
                <p><strong>{{ $t("candidateDetails.card.id") }}:</strong> {{ cert.id }}</p>
                <p>
                  <strong>{{ $t("candidateTabs.certifications.description") }}:</strong>
                  {{ cert.Certificate_description }}
                </p>
              </v-card-text>
              <v-card-actions>
                <v-btn icon @click="openEditCertificationDialog(cert)">
                  <v-icon color="primary">mdi-pencil</v-icon>
                </v-btn>
                <v-btn icon color="error" @click="confirmDeleteCertification(cert.id)">
                  <v-icon>mdi-delete</v-icon>
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-col>
        </v-row>
        <v-btn
          color="primary"
          @click="openAddCertificationDialog"
          class="mb-4 d-flex align-items-stretch"
        >
          {{ $t("candidateTabs.certifications.addButton") }}
        </v-btn>
      </v-card-text>
    </v-card>

    <v-dialog v-model="addCertificationDialog" max-width="600px">
      <v-card>
        <v-card-title>{{ $t("candidateTabs.certifications.addTitle") }}</v-card-title>
        <v-card-text>
          <v-form @submit.prevent="addCertification">
            <v-container>
              <v-row>
                <v-col cols="12" md="6">
                  <v-text-field :label="`${$t('candidateTabs.certifications.field')} *`" v-model="newCertification.Certificate" :rules="[rules.required]" outlined required></v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                  <v-textarea :label="$t('candidateTabs.certifications.descriptionLabel')" v-model="newCertification.Certificate_description" outlined></v-textarea>
                </v-col>
                <v-col cols="12">
                  <v-btn color="primary" type="submit">{{ $t("app.actions.add") }}</v-btn>
                  <v-btn variant="text" @click="closeAddCertificationDialog">{{ $t("app.actions.cancel") }}</v-btn>
                </v-col>
              </v-row>
            </v-container>
          </v-form>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-dialog v-model="editCertificationDialog" max-width="600px">
      <v-card>
        <v-card-title>{{ $t("candidateTabs.certifications.editTitle") }}</v-card-title>
        <v-card-text>
          <v-form @submit.prevent="updateCertification">
            <v-container>
              <v-row>
                <v-col cols="12" md="6">
                  <v-text-field :label="`${$t('candidateTabs.certifications.field')} *`" v-model="editCertification.Certificate" :rules="[rules.required]" outlined required></v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                  <v-textarea :label="$t('candidateTabs.certifications.descriptionLabel')" v-model="editCertification.Certificate_description" outlined></v-textarea>
                </v-col>
                <v-col cols="12">
                  <v-btn color="primary" type="submit">{{ $t("app.actions.save") }}</v-btn>
                  <v-btn variant="text" @click="closeEditCertificationDialog">{{ $t("app.actions.cancel") }}</v-btn>
                </v-col>
              </v-row>
            </v-container>
          </v-form>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import axios from "axios";
import { defineComponent } from "vue";

// Certifications CRUD tab extracted from CandidateDetails.vue.
export default defineComponent({
  name: "CandidateCertificationsTab",
  props: {
    candidateId: { type: [String, Number], required: true },
    items: { type: Array, default: () => [] },
  },
  emits: ["refresh", "notify"],
  data() {
    return {
      addCertificationDialog: false,
      newCertification: { Certificate: "", Certificate_description: "" },
      editCertificationDialog: false,
      editCertification: { id: null, Certificate: "", Certificate_description: "" },
    };
  },
  computed: {
    rules() {
      return { required: (value) => !!value || this.$t("candidateTabs.common.requiredField") };
    },
  },
  methods: {
    openAddCertificationDialog() {
      this.newCertification = { Certificate: "", Certificate_description: "" };
      this.addCertificationDialog = true;
    },
    closeAddCertificationDialog() {
      this.addCertificationDialog = false;
    },
    addCertification() {
      if (!this.newCertification.Certificate.trim()) {
        this.$emit("notify", { message: this.$t("candidateTabs.certifications.requiredEmpty"), color: "error" });
        return;
      }
      const payload = {
        id_foreign: this.candidateId,
        Certificate: this.newCertification.Certificate,
        Certificate_description: this.newCertification.Certificate_description || null,
      };
      axios
        .post("/candidati/certification", payload)
        .then(() => {
          this.$emit("notify", { message: this.$t("candidateTabs.certifications.added"), color: "success" });
          this.addCertificationDialog = false;
          this.$emit("refresh");
        })
        .catch((error) => {
          console.error("Eroare la adăugarea certificării:", error);
          this.$emit("notify", { message: this.$t("candidateTabs.certifications.addError"), color: "error" });
        });
    },
    openEditCertificationDialog(certification) {
      this.editCertification = { ...certification };
      this.editCertificationDialog = true;
    },
    closeEditCertificationDialog() {
      this.editCertificationDialog = false;
    },
    updateCertification() {
      if (!this.editCertification.Certificate.trim()) {
        this.$emit("notify", { message: this.$t("candidateTabs.certifications.requiredEmpty"), color: "error" });
        return;
      }
      const certificationId = this.editCertification.id;
      const payload = {
        id_foreign: this.candidateId,
        Certificate: this.editCertification.Certificate,
        Certificate_description: this.editCertification.Certificate_description || null,
      };
      axios
        .put(`/candidati/certification/${certificationId}`, payload)
        .then(() => {
          this.$emit("notify", { message: this.$t("candidateTabs.certifications.updated"), color: "success" });
          this.editCertificationDialog = false;
          this.$emit("refresh");
        })
        .catch((error) => {
          console.error("Eroare la actualizarea certificării:", error);
          this.$emit("notify", { message: this.$t("candidateTabs.certifications.updateError"), color: "error" });
        });
    },
    confirmDeleteCertification(id) {
      if (confirm(this.$t("candidateTabs.certifications.deleteConfirm"))) {
        this.deleteCertification(id);
      }
    },
    deleteCertification(id) {
      axios
        .delete(`/candidati/certification/${id}`)
        .then(() => {
          this.$emit("notify", { message: this.$t("candidateTabs.certifications.deleted"), color: "success" });
          this.$emit("refresh");
        })
        .catch((error) => {
          console.error("Eroare la ștergerea certificării:", error);
          this.$emit("notify", { message: this.$t("candidateTabs.certifications.deleteError"), color: "error" });
        });
    },
  },
});
</script>
